import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

const BG_VOLUME = 0.18; // low background level
const BgMusicCtx = createContext(null);

export const BackgroundMusicProvider = ({ children }) => {
  const audioRef = useRef(null);
  if (audioRef.current == null) {
    const a = new Audio();
    a.loop = true;
    a.preload = "auto";
    a.volume = BG_VOLUME;
    audioRef.current = a;
  }
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true); // start muted so autoplay is allowed
  const player = usePlayer();

  // Fetch background music URL once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site/background-music");
        if (cancelled) return;
        if (data?.url) {
          const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
          setUrl(fullUrl);
          const a = audioRef.current;
          a.src = fullUrl;
          a.muted = true;
          try {
            await a.play();
            setPlaying(true);
          } catch {
            // browser blocked even muted autoplay → wait for first user interaction
          }
        }
      } catch { /* no background music configured */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // First user interaction → unmute (the song was already playing silently)
  useEffect(() => {
    if (!url) return;
    const a = audioRef.current;
    const onFirstInteract = async () => {
      if (a.paused) {
        try { await a.play(); setPlaying(true); } catch { /* ignored */ }
      }
      // Unmute after first user gesture so the song becomes audible
      a.muted = false;
      setMuted(false);
      window.removeEventListener("pointerdown", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
    window.addEventListener("pointerdown", onFirstInteract, { once: true });
    window.addEventListener("keydown", onFirstInteract, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
  }, [url]);

  // Pause background music when an episode plays. Resume when episode pauses/stops.
  const wasPlayingBeforeEpisodeRef = useRef(false);
  useEffect(() => {
    if (!url) return;
    const a = audioRef.current;
    if (player?.playing) {
      if (!a.paused) {
        wasPlayingBeforeEpisodeRef.current = true;
        a.pause();
        setPlaying(false);
      }
    } else if (wasPlayingBeforeEpisodeRef.current) {
      wasPlayingBeforeEpisodeRef.current = false;
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [player?.playing, url]);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!url) return;
    if (a.paused) {
      try { await a.play(); setPlaying(true); a.muted = muted; } catch { /* ignored */ }
    } else {
      a.pause(); setPlaying(false);
    }
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!url) return;
    const next = !muted;
    a.muted = next;
    setMuted(next);
    // If unmuting and not currently playing, start it
    if (!next && a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const refresh = async () => {
    const { data } = await api.get("/site/background-music");
    if (data?.url) {
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setUrl(fullUrl);
      const a = audioRef.current;
      a.src = fullUrl;
      try { await a.play(); setPlaying(true); } catch { /* ignored */ }
    } else {
      setUrl(null);
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <BgMusicCtx.Provider value={{ url, available: !!url, playing, muted, togglePlay, toggleMute, refresh }}>
      {children}
    </BgMusicCtx.Provider>
  );
};

export const useBackgroundMusic = () => useContext(BgMusicCtx) || { available: false };
